<template>
  <div class="enterprise-list-page">
    <PageHeader icon="fas fa-clipboard-check">
      <template #title>Ringkasan & Proses Lab</template>
      <template #subtitle>Antrian kerja sederhana untuk pemeriksaan laboratorium klinik</template>
      <template #actions>
        <b-button variant="primary" class="mr-2" @click="$router.push('/lab-report-2/add')"><i class="fas fa-plus"></i> Input Hasil</b-button>
        <b-button variant="light" @click="loadAll"><i class="fas fa-sync-alt"></i> Muat Ulang</b-button>
      </template>
    </PageHeader>

    <div class="row mb-3">
      <div v-for="card in cards" :key="card.label" class="col-6 col-lg mb-3">
        <div class="card h-100"><div class="card-body py-4">
          <div class="text-muted small">{{ card.label }}</div>
          <div class="h2 mb-0">{{ card.value }}</div>
          <small class="text-muted">{{ card.hint }}</small>
        </div></div>
      </div>
    </div>

    <b-alert :show="setupWarnings.length > 0" variant="warning" class="mb-4">
      <strong>Konfigurasi belum lengkap:</strong> {{ setupWarnings.join(' · ') }}
      <b-button size="sm" variant="outline-warning" class="ml-2" @click="$router.push('/lab-procedure-2/list')">Lengkapi pemeriksaan</b-button>
    </b-alert>

    <b-alert :show="!!loadError" variant="danger" class="mb-4">
      <strong>Data ringkasan belum dapat dimuat.</strong> {{ loadError }}
    </b-alert>

    <b-tabs card>
      <b-tab title="Antrian Hasil" active>
        <div class="d-flex flex-wrap align-items-center mb-3">
          <b-form-select v-model="statusFilter" :options="statusOptions" class="mr-2 mb-2" style="max-width:220px" />
          <b-form-input v-model="search" placeholder="Cari pasien / nomor hasil" class="mb-2" style="max-width:300px" />
          <small class="text-muted ml-auto">Alur: Draf → Siap diverifikasi → Terverifikasi</small>
        </div>
        <b-table class="lab-worklist-table" responsive hover show-empty empty-text="Belum ada hasil pemeriksaan pada periode ini" :items="filteredWorklist" :fields="worklistFields">
          <template #cell(date)="row">{{ formatDate(row.item.date) }}</template>
          <template #cell(workflow_status)="row"><b-badge :variant="statusVariant(row.item.workflow_status)">{{ statusLabel(row.item.workflow_status) }}</b-badge></template>
          <template #cell(flags)="row">
            <b-badge v-if="row.item.has_critical_result" variant="danger">Kritis</b-badge>
            <b-badge v-else-if="row.item.has_abnormal_result" variant="warning">Abnormal</b-badge>
            <span v-else class="text-muted">—</span>
          </template>
          <template #cell(actions)="row">
            <div class="lab-worklist-actions">
              <b-button v-b-tooltip.hover title="Buka detail hasil" aria-label="Buka detail hasil" size="sm" variant="info" @click="$router.push(`/lab-report-2/detail/${row.item.id}`)"><i class="fas fa-eye"></i></b-button>
              <b-button v-if="['draft','final','corrected'].includes(row.item.workflow_status)" v-b-tooltip.hover title="Lanjutkan input atau verifikasi" aria-label="Lanjutkan input atau verifikasi" size="sm" variant="outline-primary" @click="$router.push(`/lab-report-2/edit/${row.item.id}`)"><i class="fas fa-edit"></i></b-button>
            </div>
          </template>
        </b-table>
      </b-tab>

      <b-tab title="Spesimen (Opsional)">
        <b-alert show variant="light">Dipakai hanya bila klinik mengambil atau memproses spesimen sendiri. Klinik yang hanya menerima hasil dari laboratorium rujukan dapat melewati bagian ini.</b-alert>
        <div class="card mb-4"><div class="card-body py-4">
          <div class="d-flex flex-wrap align-items-center mb-3">
            <div><h5 class="mb-1">Buat Barcode Spesimen</h5><small class="text-muted">Hubungkan spesimen dengan hasil pemeriksaan yang sudah ada.</small></div>
            <b-button v-b-toggle.create-lab-specimen variant="outline-primary" size="sm" class="ml-auto"><i class="fas fa-barcode"></i> Buat Spesimen</b-button>
          </div>
          <b-collapse id="create-lab-specimen">
            <div class="row align-items-end">
              <div class="col-lg-5"><b-form-group label="Hasil pemeriksaan *" class="mb-2"><b-form-select v-model="specimenForm.lab_id" :options="labOptions" /></b-form-group></div>
              <div class="col-lg-3"><b-form-group label="Jenis spesimen *" class="mb-2"><b-form-input v-model.trim="specimenForm.specimen_type" placeholder="Contoh: Darah EDTA" /></b-form-group></div>
              <div class="col-lg-2"><b-form-group label="Wadah" class="mb-2"><b-form-input v-model.trim="specimenForm.container_type" placeholder="Tabung EDTA" /></b-form-group></div>
              <div class="col-lg-2 mb-2"><b-button block variant="primary" :disabled="creatingSpecimen || !specimenForm.lab_id || !specimenForm.specimen_type" @click="createSpecimen"><i class="fas fa-plus"></i> Simpan</b-button></div>
            </div>
          </b-collapse>
        </div></div>
        <b-table responsive hover show-empty empty-text="Belum ada spesimen" :items="specimens" :fields="specimenFields">
          <template #cell(status)="row"><b-badge :variant="specimenVariant(row.item.status)">{{ specimenLabel(row.item.status) }}</b-badge></template>
          <template #cell(actions)="row">
            <b-button v-for="next in nextStatuses(row.item.status)" :key="next" size="sm" class="mr-1 mb-1" @click="transition(row.item, next)">{{ specimenLabel(next) }}</b-button>
          </template>
        </b-table>
      </b-tab>

      <b-tab title="Fitur Lanjutan (Opsional)" lazy>
        <AdvancedOperations :specimens="specimens" @results-ingested="loadAll" />
      </b-tab>

      <b-tab title="Hubungan Modul">
        <div class="row">
          <div v-for="(step, index) in integrationSteps" :key="step.title" class="col-md mb-3">
            <div class="card h-100"><div class="card-body">
              <b-badge variant="primary" pill>{{ index + 1 }}</b-badge>
              <h5 class="mt-3">{{ step.title }}</h5><p class="text-muted mb-0">{{ step.text }}</p>
            </div></div>
          </div>
        </div>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import ApiService from '@/core/services/api.service.js'
import AdvancedOperations from '@/component/lab-reports-2/AdvancedOperations.vue'
import { SET_BREADCRUMB } from '@/core/services/store/breadcrumbs.module'

export default {
  components: { AdvancedOperations },
  data: () => ({
    loading: false,
    creatingSpecimen: false,
    loadError: '',
    dashboard: { reports: {}, tat_minutes: {}, setup: {}, worklist: [] },
    specimens: [], statusFilter: '', search: '',
    specimenForm: { lab_id: null, specimen_type: 'Darah EDTA', container_type: 'Tabung EDTA' },
    statusOptions: [{ value: '', text: 'Semua status' }, { value: 'draft', text: 'Draf' }, { value: 'final', text: 'Siap diverifikasi' }, { value: 'verified', text: 'Terverifikasi' }, { value: 'corrected', text: 'Koreksi' }, { value: 'cancelled', text: 'Dibatalkan' }],
    worklistFields: [{ key: 'date', label: 'Tanggal' }, { key: 'accession_number', label: 'No. Hasil' }, { key: 'patient_name', label: 'Pasien' }, { key: 'lab_category_name', label: 'Kategori' }, { key: 'result_count', label: 'Parameter' }, { key: 'flags', label: 'Penanda' }, { key: 'workflow_status', label: 'Status' }, { key: 'actions', label: 'Aksi', thClass: 'lab-worklist-actions-column', tdClass: 'lab-worklist-actions-column' }],
    specimenFields: [{ key: 'barcode', label: 'Barcode' }, { key: 'accession_number', label: 'No. hasil' }, { key: 'patient_name', label: 'Pasien' }, { key: 'specimen_type', label: 'Spesimen' }, { key: 'status', label: 'Status' }, { key: 'actions', label: 'Proses berikutnya' }],
    integrationSteps: [
      { title: 'Kategori Lab', text: 'Mengelompokkan pemeriksaan agar katalog dan laporan mudah dicari.' },
      { title: 'Pemeriksaan & Tarif', text: 'Mendefinisikan layanan, harga, kategori, komisi, dan template parameternya.' },
      { title: 'Template Laporan', text: 'Menentukan parameter, satuan, dan nilai rujukan yang otomatis masuk saat pemeriksaan dipilih.' },
      { title: 'Input Hasil', text: 'Memilih pasien dan pemeriksaan, lalu mengisi hasil dengan nilai rujukan yang sudah tersambung.' },
      { title: 'Hasil Pemeriksaan', text: 'Menyimpan draf, verifikasi, cetak, koreksi, dan riwayat hasil pasien.' },
    ]
  }),
  computed: {
    cards() { const r = this.dashboard.reports || {}; return [
      { label: 'Draf', value: r.draft || 0, hint: 'Belum selesai diinput' },
      { label: 'Siap diverifikasi', value: r.ready || 0, hint: 'Perlu pemeriksaan akhir' },
      { label: 'Terverifikasi', value: r.verified || 0, hint: 'Siap dicetak/dibagikan' },
      { label: 'Nilai kritis', value: r.critical || 0, hint: 'Perlu tindak lanjut' },
      { label: 'Rata-rata selesai', value: `${(this.dashboard.tat_minutes || {}).average || 0} mnt`, hint: 'Waktu input sampai verifikasi' },
    ] },
    setupWarnings() { const s = this.dashboard.setup || {}, warnings = []; if (!s.categories) warnings.push('Belum ada Kategori Lab'); if (!s.procedures) warnings.push('Belum ada Pemeriksaan & Tarif'); if (s.templates > s.configured_templates) warnings.push(`${s.templates - s.configured_templates} template belum memiliki parameter`); return warnings },
    labOptions() { return [{ value: null, text: 'Pilih hasil pemeriksaan', disabled: true }, ...(this.dashboard.worklist || []).map(item => ({ value: item.id, text: `${item.accession_number} — ${item.patient_name}` }))] },
    filteredWorklist() { const q = this.search.toLowerCase().trim(); return (this.dashboard.worklist || []).filter(row => (!this.statusFilter || row.workflow_status === this.statusFilter) && (!q || `${row.patient_name} ${row.accession_number}`.toLowerCase().includes(q))) }
  },
  methods: {
    async loadAll() {
      this.loading = true
      this.loadError = ''
      try {
        const dashboardResponse = await ApiService.get('lab-operations/dashboard')
        if (!dashboardResponse || dashboardResponse.status >= 400 || !dashboardResponse.data || !dashboardResponse.data.data) {
          this.loadError = dashboardResponse?.data?.message || 'Periksa hak akses dan konfigurasi modul Lab.'
        } else {
          const payload = dashboardResponse.data.data
          this.dashboard = {
            reports: payload.reports || {},
            tat_minutes: payload.tat_minutes || {},
            setup: payload.setup || {},
            worklist: payload.worklist || []
          }
        }

        const specimenResponse = await ApiService.get('lab-specimens')
        this.specimens = specimenResponse && specimenResponse.status < 400 && specimenResponse.data
          ? (specimenResponse.data.data || [])
          : []
      } catch (error) {
        this.loadError = error.response?.data?.message || 'Periksa koneksi dan konfigurasi modul Lab.'
      } finally {
        this.loading = false
      }
    },
    formatDate(value) { return value ? new Date(value).toLocaleDateString('id-ID') : '—' },
    statusLabel(status) { return { draft: 'Draf', final: 'Siap diverifikasi', verified: 'Terverifikasi', corrected: 'Koreksi', cancelled: 'Dibatalkan' }[status] || status },
    statusVariant(status) { return { draft: 'secondary', final: 'primary', verified: 'success', corrected: 'warning', cancelled: 'danger' }[status] || 'secondary' },
    specimenLabel(status) { return { ordered: 'Dipesan', collected: 'Diambil', received: 'Diterima', rejected: 'Ditolak', processing: 'Diproses', completed: 'Selesai' }[status] || status },
    specimenVariant(status) { return { ordered: 'secondary', collected: 'info', received: 'primary', rejected: 'danger', processing: 'warning', completed: 'success' }[status] || 'secondary' },
    nextStatuses(status) { return { ordered: ['collected', 'rejected'], collected: ['received', 'rejected'], received: ['processing', 'rejected'], processing: ['completed', 'rejected'] }[status] || [] },
    async createSpecimen() {
      this.creatingSpecimen = true
      try {
        const response = await ApiService.post('lab-specimens', this.specimenForm)
        await Swal.fire('Spesimen dibuat', `${response.data?.message || 'Barcode spesimen berhasil dibuat.'}\n${response.data?.data?.barcode || ''}`, 'success')
        this.specimenForm = { lab_id: null, specimen_type: 'Darah EDTA', container_type: 'Tabung EDTA' }
        await this.loadAll()
      } catch (error) {
        const data = error?.response?.data
        const message = data?.errors ? Object.values(data.errors).flat().join(' ') : (data?.message || 'Spesimen belum dapat dibuat.')
        await Swal.fire('Belum dapat membuat spesimen', message, 'error')
      } finally { this.creatingSpecimen = false }
    },
    async transition(item, status) { let notes = null; if (status === 'rejected') { const answer = await Swal.fire({ title: 'Alasan penolakan', input: 'textarea', showCancelButton: true, inputValidator: value => !value || value.length < 5 ? 'Minimal 5 karakter' : undefined }); if (!answer.isConfirmed) return; notes = answer.value } await ApiService.post(`lab-specimens/${item.id}/transition`, { status, notes }); await this.loadAll() }
  },
  mounted() { this.$store.dispatch(SET_BREADCRUMB, [{ title: 'Pemeriksaan Lab' }, { title: 'Ringkasan & Proses' }]); this.loadAll() }
}
</script>

<style>
#kt_content .lab-worklist-table th.lab-worklist-actions-column,
#kt_content .lab-worklist-table td.lab-worklist-actions-column {
  width: 104px;
  min-width: 104px;
  text-align: center;
  white-space: nowrap;
}

#kt_content .lab-worklist-table .lab-worklist-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  white-space: nowrap;
}

#kt_content .lab-worklist-table .lab-worklist-actions .btn.btn-sm {
  flex: 0 0 36px;
  margin: 0 !important;
}

@media (max-width: 767.98px) {
  #kt_content .lab-worklist-table th.lab-worklist-actions-column,
  #kt_content .lab-worklist-table td.lab-worklist-actions-column {
    width: 96px;
    min-width: 96px;
  }
}
</style>
